import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import type { BusinessCount } from '@/components/Table/Table';
import Button from '@/components/Button/newButtons';
import type {
	Business,
	BusinessQuery
} from '@/modules/dashboard/dashboardTypes';
import {
	dashboardGetBusinessesRequest,
	dashboardSelectBusiness,
	dashboardSetQuery
} from '@/modules/dashboard/dashboardActions';
import { Tabs } from '@/components/atoms/Tabs';
import { cn } from '@/lib/classNames';
import {
	ExclamationMarkRed,
	Menu,
	XIcon,
	DropdownArrowDown
} from '@/resources/assets/images';
import { Dialog } from '@/components/atoms/Dialog';
import Loader, { Status } from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import {
	searchAddSearchRequest,
	searchAddSearchReset
} from '@/modules/search/searchActions';
import { useMapboxKeyQuery } from '@/hooks/useMapBoxKeyQuery';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { useToast } from '@/components/atoms/Toast';
import type { Search } from '@/modules/search/searchTypes';
import SearchTab from './SearchTab/SearchTab';
import SavedSearchesTab from './SavedSearchesTab/SavedSearchesTab';
import { FilterTabs, useFiltersTab } from './src/hooks/useFiltersTab';
import {
	SearchTabContextProvider,
	useSearchTabContext
} from './SearchTab/SearchTabContext';
import { useMenuToggle } from './src/hooks/useMenuToggle';

interface SearchBusinessesFilterProps {
	setFiltersCleared?: (cleared: boolean) => void; // Updated to accept a boolean value
	handleSwitchView?: (view: 'businesses' | 'map') => void;
	businessCount?: number | BusinessCount;
}

const SearchBusinessesFilter: React.FC<SearchBusinessesFilterProps> = ({
	setFiltersCleared,
	handleSwitchView,
	businessCount
}) => {
	const menuRef = useRef<HTMLDivElement>(null);

	const { isMenuOpening, isMenuOpen, handleMenuToggle } =
		useMenuToggle(menuRef);

	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const dispatch = useDispatch();

	const { currentTab, handleChangeFilterTab } = useFiltersTab();
	const [saveSearchModalOpen, setSaveSearchModalOpen] =
		useState<boolean>(false);
	const [filtersCleared, setLocalFiltersCleared] = useState<boolean>(true);

	const selectBusiness = useCallback(
		(business: Business | null) =>
			dispatch(dashboardSelectBusiness(business)),
		[dispatch]
	);
	const setQuery = useCallback(
		(q: BusinessQuery) => dispatch(dashboardSetQuery({ ...q, user })),
		[dispatch, user]
	);
	const getBusinesses = useCallback(
		(q: BusinessQuery) => dispatch(dashboardGetBusinessesRequest(q)),
		[dispatch]
	);

	const filtersCount = useMemo(() => {
		let count = 0;

		if (query?.contactedYes || query?.contactedNo) {
			count++;
		}

		if (query?.scheduledYes || query?.scheduledNo) {
			count++;
		}

		if (query?.lead !== undefined) {
			count++;
		}

		if (query?.includeEmailsYes) {
			count++;
		}

		if (query?.includeFBMessenger) {
			count++;
		}

		if (query?.includeIGMessenger) {
			count++;
		}

		if (query?.includePhoneNumber) {
			count++;
		}

		if (query?.includeWebsitesYes) {
			count++;
		}

		if (query?.includeWebsitesNo) {
			count++;
		}

		if (query?.chains || query?.nonChains) {
			count++;
		}

		if (query?.countOne) {
			count++;
		}

		if (query?.countTwoTen) {
			count++;
		}

		if (query?.countTenPlus) {
			count++;
		}

		if (query?.includePOIs !== undefined) {
			count++;
		}

		if (query?.excludePOIs !== undefined) {
			count++;
		}

		if (
			query?.coordinates ||
			(query.country && query.state) ||
			query.country
		) {
			count++;
		}

		if (query?.salesVolumeLessThan5M) {
			count++;
		}

		if (query?.salesVolume5To10M) {
			count++;
		}

		if (query?.salesVolume10To20M) {
			count++;
		}

		if (query?.salesVolume20To50M) {
			count++;
		}

		if (query?.salesVolume50To100M) {
			count++;
		}

		if (query?.salesVolumeOver100M) {
			count++;
		}

		// Number of Employees filters
		if (query?.employees1To49) {
			count++;
		}
		if (query?.employees50To99) {
			count++;
		}
		if (query?.employees100To249) {
			count++;
		}
		if (query?.employees250To499) {
			count++;
		}
		if (query?.employeesOver500) {
			count++;
		}
		if (query?.sqftSmall || query?.sqftMedium || query?.sqftLarge) {
			count++;
		}

		if (query?.searches?.length > 0) {
			count += query.searches.length;
		}

		return count;
	}, [
		query?.chains,
		query?.contactedNo,
		query?.contactedYes,
		query?.coordinates,
		query?.countOne,
		query?.countTenPlus,
		query?.countTwoTen,
		query?.country,
		query?.employees100To249,
		query?.employees1To49,
		query?.employees250To499,
		query?.employees50To99,
		query?.employeesOver500,
		query?.excludePOIs,
		query?.includeEmailsYes,
		query?.includeFBMessenger,
		query?.includeIGMessenger,
		query?.includePOIs,
		query?.includePhoneNumber,
		query?.includeWebsitesNo,
		query?.includeWebsitesYes,
		query?.lead,
		query?.nonChains,
		query?.salesVolume10To20M,
		query?.salesVolume20To50M,
		query?.salesVolume50To100M,
		query?.salesVolume5To10M,
		query?.salesVolumeLessThan5M,
		query?.salesVolumeOver100M,
		query?.scheduledNo,
		query?.scheduledYes,
		query?.searches?.length,
		query?.sqftLarge,
		query?.sqftMedium,
		query?.sqftSmall,
		query?.state
	]);

	const handleChangeFilters = useCallback(
		(businessQuery: BusinessQuery) => {
			setQuery({
				...businessQuery,
				skip: 0,
				limit: 50,
				count: true,
				maxDistance: businessQuery.maxDistance || 40
			});
			selectBusiness(null);
		},
		[selectBusiness, setQuery]
	);

	const handleGetBusiness = useCallback(
		(businessQuery: BusinessQuery) => {
			getBusinesses(businessQuery);
		},
		[getBusinesses]
	);

	const handleChangeTabToSearch = useCallback(() => {
		handleChangeFilterTab('search');
	}, [handleChangeFilterTab]);

	const handleChangeTab = useCallback(
		(tab: string) => {
			handleChangeFilterTab(tab as FilterTabs);
		},
		[handleChangeFilterTab]
	);

	const handleOpenModal = () => {
		setSaveSearchModalOpen(true);
	};

	const handleCloseModal = () => {
		setSaveSearchModalOpen(false);
	};

	const { isSmaller: isMobile } = useIsScreenWidthSmallerThan(600);

	useEffect(() => {
		const areFiltersCleared = filtersCount === 0;

		if (areFiltersCleared !== filtersCleared) {
			setLocalFiltersCleared(areFiltersCleared);
			setFiltersCleared?.(areFiltersCleared); // Notify the parent only when state changes
		}
	}, [query, filtersCleared, setFiltersCleared, filtersCount]);

	return (
		<SearchTabContextProvider>
			<div
				ref={menuRef}
				className={cn(
					'relative flex h-9 w-full items-center',
					'lg:h-full lg:w-full lg:max-w-64 lg:items-start'
				)}
			>
				<button
					type="button"
					className="flex w-full items-center gap-4 rounded-md border border-dm-mid-grey bg-dm-black p-2 lg:hidden lg:h-0 lg:w-0"
					onClick={handleMenuToggle}
				>
					{isMenuOpen ? (
						isMobile ? (
							<DropdownArrowDown className="size-6 fill-dm-white" />
						) : (
							<XIcon className="size-6 fill-dm-white" />
						)
					) : (
						<Menu className="size-6 fill-dm-white" />
					)}

					<span className="inline-flex items-center gap-2 text-sm text-dm-white">
						Filters
						{filtersCount > 0 && (
							<span className="flex aspect-square h-5 w-auto items-center justify-center rounded-md border border-dm-gold text-sm text-dm-gold">
								{filtersCount}
							</span>
						)}
					</span>
				</button>

				<nav
					data-is-menu-open={isMenuOpen}
					data-is-menu-opening={isMenuOpening}
					className={cn(
						'absolute left-0 top-0 z-50 flex h-0 w-0 translate-y-9 flex-col gap-12 p-px transition-all',
						'data-[is-menu-opening=true]:overflow-hidden',
						'data-[is-menu-open=true]:h-auto data-[is-menu-open=true]:w-full data-[is-menu-open=true]:min-w-[70vw]',
						'lg:relative lg:h-full lg:w-full lg:translate-y-0 lg:justify-between',
						"before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:origin-top-left before:scale-0 before:rounded-xl before:bg-dm-black1/[.98] before:transition before:content-['']",
						'data-[is-menu-open=true]:before:scale-100 data-[is-menu-open=true]:before:rounded-md data-[is-menu-open=true]:before:border data-[is-menu-open=true]:before:border-dm-gray',
						'lg:before:content-none lg:data-[is-menu-open=true]:h-full lg:data-[is-menu-open=true]:w-full lg:data-[is-menu-open=true]:min-w-0' // reset mobile state on large screens
					)}
				>
					<section
						data-is-menu-open={isMenuOpen}
						className={cn(
							'hidden h-full w-full flex-col gap-4',
							'data-[is-menu-open=true]:flex',
							'lg:flex lg:gap-6'
						)}
					>
						<Tabs.Root
							value={currentTab}
							onValueChange={handleChangeTab}
							className={cn(
								'relative flex h-full min-h-[31rem] w-full flex-col overflow-auto rounded-lg bg-dm-black lg:min-h-0 lg:max-w-64'
							)}
						>
							<Tabs.List
								marginLess
								className="items-center justify-center"
							>
								<Tabs.Trigger
									value="search"
									className="gap-2 p-4 capitalize"
								>
									Search
								</Tabs.Trigger>
								<Tabs.Trigger
									value="saved-filters"
									className="p-4 capitalize"
								>
									Saved Filters
								</Tabs.Trigger>
							</Tabs.List>

							<Tabs.Content
								value="search"
								className="flex flex-col items-center justify-between"
							>
								<SearchTab
									handleSwitchView={handleSwitchView}
									onFilterChange={handleChangeFilters}
									onGetBusiness={handleGetBusiness}
								/>

								<section className="flex flex-col items-center justify-center p-8">
									<SaveSearchModal
										isOpen={saveSearchModalOpen}
										onOpenModal={handleOpenModal}
										onCloseModal={handleCloseModal}
										isDisabled={filtersCount === 0}
										businessCount={businessCount}
										handleMenuToggle={handleMenuToggle}
									/>
								</section>
							</Tabs.Content>

							<Tabs.Content
								value="saved-filters"
								className="relative overflow-auto"
							>
								<SavedSearchesTab
									onChangeTabToSearch={
										handleChangeTabToSearch
									}
									onFilterChange={handleChangeFilters}
									onGetBusiness={handleGetBusiness}
								/>
							</Tabs.Content>
						</Tabs.Root>
					</section>
				</nav>
			</div>
		</SearchTabContextProvider>
	);
};

type SaveSearchModalProps = {
	isOpen: boolean;
	onOpenModal: () => void;
	onCloseModal: () => void;
	isDisabled: boolean;
	businessCount?: number | BusinessCount;
	handleMenuToggle?: () => void;
};

const SaveSearchModal = ({
	isOpen,
	onOpenModal,
	onCloseModal,
	isDisabled,
	businessCount,
	handleMenuToggle
}: SaveSearchModalProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const { query, searchIsSaving, saveError } = useSelector(
		(state: RootState) => ({
			query: state.dashboard.get('query').toJS(),
			searchIsSaving: state.search.get('searchIsSaving'),
			saveError: state.search.get('saveError')
		}),
		shallowEqual
	);
	const { isSmaller: isMobile } = useIsScreenWidthSmallerThan(600);

	const { selectedLocation, setSelectedLocation } = useSearchTabContext();

	const { data: mapboxKeyData } = useMapboxKeyQuery();
	const mapboxAPIKey = mapboxKeyData?.data?.data.mapBoxAPIKey;

	const { toast } = useToast();
	const dispatch = useDispatch();

	const addSearch = (s: Search) => dispatch(searchAddSearchRequest(s));
	const resetSaveStatus = useCallback(
		() => dispatch(searchAddSearchReset()),
		[dispatch]
	);

	const reverseGeocode = async (
		coordinates: [number, number]
	): Promise<string | null> => {
		try {
			// Example using Mapbox Geocoding API
			const [longitude, latitude] = coordinates;
			const accessToken = mapboxAPIKey;
			const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${accessToken}`;

			// Make the API request using fetch
			const response = await fetch(url);
			if (response.status !== 200) {
				throw new Error(`Geocoding API error: ${response.statusText}`);
			}

			const data = await response.json();
			const { features } = data;

			// Extract city and state from the response
			const place = features.find((f: any) =>
				f.place_type.includes('place')
			)?.text;
			const region = features.find((f: any) =>
				f.place_type.includes('region')
			)?.text;

			if (place && region) {
				return `${place}, ${region}`; // Return "City, State"
			}

			return null; // Fallback if city or state is not found
		} catch (error) {
			return null;
		}
	};

	const parseCoordinates = (): [number, number] | undefined => {
		if (typeof query?.coordinates === 'string') {
			try {
				// Attempt to parse the coordinates string into an array
				const parsedCoordinates = JSON.parse(query.coordinates);

				// Ensure the parsed result is an array of two numbers
				if (
					Array.isArray(parsedCoordinates) &&
					parsedCoordinates.length === 2
				) {
					const [longitude, latitude] = parsedCoordinates;

					if (
						typeof longitude === 'number' &&
						typeof latitude === 'number'
					) {
						return [longitude, latitude];
					}
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to parse coordinates:', error);
			}
		}
	};

	const parseLocationName = async (
		coordinates: [number, number] | undefined
	) => {
		const isNonDescriptiveName = /^[a-f0-9]{24}$/.test(
			selectedLocation || ''
		); // Matches a 24-character hexadecimal string

		// Fetch city and state if location is "Current location"
		if (
			(selectedLocation === 'Current location' || isNonDescriptiveName) &&
			coordinates
		) {
			const cityState = (await reverseGeocode(coordinates)) || '';

			if (!cityState) return;

			setSelectedLocation(cityState); // Update the context with the new location name
			return cityState;
		}

		return selectedLocation;
	};

	const handleCloseModal = useCallback(
		(value?: boolean) => {
			if (value) return;

			resetSaveStatus();
			onCloseModal();
		},
		[onCloseModal, resetSaveStatus]
	);

	const handleSaveSearch = async () => {
		const coordinates = parseCoordinates();

		const locationName = await parseLocationName(coordinates);

		const trimmedName = inputRef.current?.value.trim();

		if (!trimmedName) {
			toast({
				title: 'Saved search name cannot be empty',
				variant: 'error'
			});
			return;
		}

		const search: Search = {
			params: query?.searches,
			name: trimmedName,
			location: {
				coordinates: coordinates || [] // Ensure coordinates are saved under location
			},
			filters: {
				...query,
				locationId: locationName,
				sort: undefined,
				sortField: undefined
			}
		};

		sendAnalytics({
			type: 'track',
			properties: {
				search
			},
			eventName: 'User saved a new search'
		});

		addSearch(search);

		handleCloseModal();
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={handleCloseModal}>
			<Dialog.Trigger asChild>
				<div className="flex gap-4">
					<Button
						variant="secondary"
						size="large"
						disabled={isDisabled}
						onClick={onOpenModal}
					>
						Save filters
					</Button>
					{
						// This button shows only on mobile
						isMobile && (businessCount as number) > 0 && (
							<Button
								variant="primary"
								size="large"
								disabled={isDisabled}
								onClick={handleMenuToggle}
							>
								Show {businessCount} Results
							</Button>
						)
					}
				</div>
			</Dialog.Trigger>

			<Dialog.Portal>
				<Dialog.Content className="gap-8">
					<h1 className="text-3xl font-light">Save Filters</h1>

					<section className="flex flex-col gap-4">
						<label
							htmlFor="save-search-name"
							className="font-medium text-dark-grey"
						>
							Name
						</label>
						<input
							className="w-full"
							id="save-search-name"
							ref={inputRef}
						/>
					</section>

					<section className="flex w-full justify-end gap-4">
						<Button variant="secondary" onClick={onCloseModal}>
							Cancel
						</Button>

						<Button
							data-is-error={saveError}
							data-is-loading={searchIsSaving}
							className={cn(
								'data-[is-error=true]:border-2 data-[is-error=true]:border-light-danger data-[is-error=true]:text-light-danger',
								'data-[is-error=true]:cursor-not-allowed data-[is-loading=true]:cursor-not-allowed'
							)}
							onClick={handleSaveSearch}
							disabled={searchIsSaving}
						>
							{searchIsSaving && (
								<Loader status={Status.LOADING} />
							)}

							{saveError && (
								<span>
									<ExclamationMarkRed className="h-3.5 w-3.5 [&_mask]:hidden [&_path]:fill-light-danger" />
								</span>
							)}

							{!searchIsSaving && !saveError && 'Save'}
						</Button>
					</section>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export default SearchBusinessesFilter;
